// âœ… REFACTORED FILE (V6.8.0-New-Routes)
// FILE: src/routes.js
// REASON: (CEO Request) Add new Public and Admin routes
// 1. (NEW) Added public routes for Brokers and Education pages.
// 2. (NEW) Added admin routes for BrokerManager and EducationManager.
// 3. (Preserved) Kept all V6.7.2 logic, including OCR route and hotfixes.

import React, { lazy, useEffect } from 'react';
// (Preserved V6.4)
import { Navigate, Outlet, useLocation } from 'react-router-dom';

// (Preserved V6.4)
import { AppProvider } from './providers/AppContext';

// (Preserved V6.7-TMA)
import { TelegramProvider } from './providers/TelegramProvider';

import { RouteErrorBoundary } from './routes/RouteErrorBoundary';
import { captureReferralFromLocation } from './features/resellers/utils/referralAttribution';
import { lazyWithRetry } from './utils/lazyWithRetry';

// (Preserved V6.4) - SCALABILITY: Lazy Loading Heavy Views
const AppLayout = lazyWithRetry(() => import('./components/layout/AppLayout'), m => ({ default: m.AppLayout }));
const AuthProviderLayout = lazy(() => import('./routes/AuthProviderLayout').then(m => ({ default: m.AuthProviderLayout })));
const PrivateRoute = lazy(() => import('./routes/PrivateRoute'));
const PrivateProviderLayout = lazy(() => import('./routes/PrivateProviderLayout').then(m => ({ default: m.PrivateProviderLayout })));
const CalendarProviderLayout = lazy(() => import('./routes/CalendarProviderLayout').then(m => ({ default: m.CalendarProviderLayout })));
const AdminRoute = lazy(() => import('./routes/AdminRoute'));
const FeatureRoute = lazy(() => import('./routes/FeatureRoute'));
const BacktestsAccessRoute = lazy(() => import('./routes/BacktestsAccessRoute'));
const SubscriptionRoute = lazy(() => import('./routes/SubscriptionRoute'));
const LandingPage = lazy(() => import('./features/landing/pages/LandingPage').then(m => ({ default: m.LandingPage })));
const PublicDemoPage = lazy(() => import('./features/landing/pages/PublicDemoPage').then(m => ({ default: m.PublicDemoPage })));
const AuthPage = lazy(() => import('./features/authentication/pages/AuthPage').then(m => ({ default: m.AuthPage })));
const ForgotPasswordPage = lazy(() => import('./features/authentication/pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('./features/authentication/pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const VerifyEmailPage = lazy(() => import('./features/authentication/pages/VerifyEmailPage').then(m => ({ default: m.VerifyEmailPage })));
const PendingApprovalPage = lazy(() => import('./features/authentication/pages/PendingApprovalPage').then(m => ({ default: m.PendingApprovalPage })));
const DashboardPage = lazyWithRetry(() => import('./features/dashboard/pages/DashboardPage'), m => ({ default: m.DashboardPage }));
const CalendarView = lazyWithRetry(() => import('./features/dashboard/components/CalendarView'), m => ({ default: m.CalendarView }));
const SessionCockpitPage = lazy(() => import('./features/session/pages/SessionCockpitPage').then(m => ({ default: m.SessionCockpitPage })));
const AnalyticsPage = lazy(() => import('./features/analytics/pages/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));
const ReportCardPage = lazy(() => import('./features/reportCard/pages/ReportCardPage').then(m => ({ default: m.ReportCardPage })));
const BacktestsPage = lazy(() => import('./features/backtesting/pages/BacktestsPage').then(m => ({ default: m.BacktestsPage || m.default })));
const TradingSystemPage = lazy(() => import('./features/tradingSystems/pages/TradingSystemPage').then(m => ({ default: m.TradingSystemPage })));

const PlaybooksPage = lazy(() => import('./features/playbooks/pages/PlaybooksPage'));
const PlaybookDetailPage = lazy(() => import('./features/playbooks/pages/PlaybookDetailPage').then(m => ({ default: m.PlaybookDetailPage })));
const PlaybookTradesDrilldownPage = lazy(() => import('./features/playbooks/pages/PlaybookTradesDrilldownPage').then(m => ({ default: m.PlaybookTradesDrilldownPage })));
const MissedTradesLogPage = lazy(() => import('./features/playbooks/pages/MissedTradesLogPage').then(m => ({ default: m.MissedTradesLogPage })));
const MissedTradeDetailPage = lazy(() => import('./features/playbooks/pages/MissedTradeDetailPage').then(m => ({ default: m.MissedTradeDetailPage })));
const MindMapPage = lazy(() => import('./features/mindmap/pages/MindMapPage'));
const DevWhiteboardPage = process.env.NODE_ENV === 'development'
    ? lazy(() => import('./features/mindmap/pages/WhiteboardDevPage'))
    : null;
const NotebooksPage = lazy(() => import('./features/notebooks/pages/NotebooksPage').then(m => ({ default: m.NotebooksPage })));
const SettingsPage = lazyWithRetry(() => import('./features/settings/pages/SettingsPage'), m => ({ default: m.SettingsPage }));
const LearnHubPage = lazy(() => import('./features/help/pages/LearnHubPage').then(m => ({ default: m.LearnHubPage })));
const LearnGettingStartedPage = lazy(() => import('./features/help/pages/LearnGettingStartedPage').then(m => ({ default: m.LearnGettingStartedPage })));
const LearnFaqPage = lazy(() => import('./features/help/pages/LearnFaqPage').then(m => ({ default: m.LearnFaqPage })));
const LearnTopicPage = lazy(() => import('./features/help/pages/LearnTopicPage').then(m => ({ default: m.LearnTopicPage })));
const SharedJournalPage = lazy(() => import('./features/sharing/pages/SharedJournalPage').then(m => ({ default: m.SharedJournalPage })));
const SharedMindMapPage = lazy(() => import('./features/sharing/pages/SharedMindMapPage').then(m => ({ default: m.SharedMindMapPage })));
const TermsPage = lazy(() => import('./features/legal/pages/LegalPage').then(m => ({ default: m.TermsPage })));
const CookiePolicyPage = lazy(() => import('./features/legal/pages/LegalPage').then(m => ({ default: m.CookiePolicyPage })));
const BrokersPage = lazy(() => import('./features/brokers/pages/BrokersPage').then(m => ({ default: m.BrokersPage })));
const ResellerLandingPage = lazy(() => import('./features/resellers/pages/ResellerLandingPage').then(m => ({ default: m.ResellerLandingPage })));
const EducationCategoryPage = lazy(() => import('./features/education/pages/EducationCategoryPage').then(m => ({ default: m.EducationCategoryPage })));
const EducationPostPage = lazy(() => import('./features/education/pages/EducationPostPage').then(m => ({ default: m.EducationPostPage })));
const AdminPage = lazy(() => import('./features/admin/pages/AdminPage').then(m => ({ default: m.AdminPage })));
const AdminOverview = lazy(() => import('./features/admin/components/AdminOverview').then(m => ({ default: m.AdminOverview })));
const AdminSecurityManager = lazy(() => import('./features/admin/components/AdminSecurityManager').then(m => ({ default: m.AdminSecurityManager })));
const UserManagementDashboard = lazy(() => import('./features/admin/components/UserManagementDashboard').then(m => ({ default: m.UserManagementDashboard })));
const SubscriptionManager = lazy(() => import('./features/admin/components/SubscriptionManager').then(m => ({ default: m.SubscriptionManager })));
const BillingContactManager = lazy(() => import('./features/admin/components/BillingContactManager').then(m => ({ default: m.BillingContactManager })));
const LinksManagerPage = lazy(() => import('./features/admin/components/LinksManagerPage').then(m => ({ default: m.LinksManagerPage })));
const FeatureManager = lazy(() => import('./features/admin/components/FeatureManager').then(m => ({ default: m.FeatureManager })));
const BacktestAccessManager = lazy(() => import('./features/admin/components/BacktestAccessManager').then(m => ({ default: m.BacktestAccessManager })));
const NotificationManager = lazy(() => import('./features/admin/components/NotificationManager').then(m => ({ default: m.NotificationManager })));
const AdminNotebook = lazy(() => import('./features/admin/components/AdminNotebook').then(m => ({ default: m.AdminNotebook })));
const LegalContentManager = lazy(() => import('./features/admin/components/LegalContentManager').then(m => ({ default: m.LegalContentManager })));
const ExchangeRateManager = lazy(() => import('./features/admin/components/ExchangeRateManager').then(m => ({ default: m.ExchangeRateManager })));
const CalendarApiManager = lazy(() => import('./features/admin/components/CalendarApiManager').then(m => ({ default: m.CalendarApiManager })));
const PublicAssetManager = lazy(() => import('./features/admin/components/PublicAssetManager').then(m => ({ default: m.PublicAssetManager })));
const BrokerManager = lazy(() => import('./features/admin/components/BrokerManager').then(m => ({ default: m.BrokerManager })));
const EducationManager = lazy(() => import('./features/admin/components/EducationManager').then(m => ({ default: m.EducationManager })));
const ResellerManager = lazy(() => import('./features/admin/components/resellers/ResellerManager').then(m => ({ default: m.ResellerManager })));
const ResellerDashboardPage = lazy(() => import('./features/resellers/pages/ResellerDashboardPage').then(m => ({ default: m.ResellerDashboardPage })));

// Removed OCR Manager

const ReferralAttributionWatcher = () => {
    const location = useLocation();

    useEffect(() => {
        captureReferralFromLocation({
            search: location.search,
            sourceUrl: typeof window !== 'undefined' ? window.location.href : '',
            landingPath: location.pathname,
        });
    }, [location.pathname, location.search]);

    return null;
};

// (Preserved V6.7-TMA)
const BaseProviderLayout = () => {
  return (
    <TelegramProvider>
      <AppProvider>
        <ReferralAttributionWatcher />
        <Outlet />
      </AppProvider>
    </TelegramProvider>
  );
};

const PublicProviderLayout = () => <Outlet />;

// (Preserved V6.4)
export const routesConfig = [
    {
        element: <BaseProviderLayout />,
        errorElement: <RouteErrorBoundary />,
        children: [
            {
                element: <PublicProviderLayout />,
                children: [
                    // (Preserved V6.4) Public routes
                    {
                        path: "/",
                        element: <Navigate to="/welcome" replace />,
                    },
                    {
                        path: "/welcome",
                        element: <LandingPage />,
                    },
                    {
                        element: <CalendarProviderLayout />,
                        children: [
                            {
                                path: "/journal/:shareId",
                                element: <SharedJournalPage />,
                            },
                        ],
                    },
                    {
                        path: "/login",
                        element: <Navigate to="/auth?type=login" replace />,
                    },
                    {
                        path: "/register",
                        element: <Navigate to="/auth?type=register" replace />,
                    },

                    // ================== (NEW) V6.8.0 PUBLIC ROUTES START ==================
                    {
                        path: "/brokers",
                        element: <BrokersPage />,
                    },
                    {
                        path: "/resellers",
                        element: <ResellerLandingPage />,
                    },
                    {
                        path: "/education/:category",
                        element: <EducationCategoryPage />,
                    },
                    {
                        path: "/education/post/:postId",
                        element: <EducationPostPage />,
                    },
                    {
                        path: "/mindmap/:shareId",
                        element: <SharedMindMapPage />,
                    },
                    {
                        path: "/terms",
                        element: <TermsPage />,
                    },
                    {
                        path: "/cookies",
                        element: <CookiePolicyPage />,
                    },
                    ...(process.env.NODE_ENV === 'development' && DevWhiteboardPage ? [
                        {
                            path: "/dev/whiteboard",
                            element: <DevWhiteboardPage />,
                        },
                    ] : []),
                    // ================== (NEW) V6.8.0 PUBLIC ROUTES END ====================
                ],
            },

            {
                element: <AuthProviderLayout />,
                children: [
                    {
                        path: "/auth",
                        element: <AuthPage />,
                    },
                    {
                        path: "/auth/forgot-password",
                        element: <ForgotPasswordPage />,
                    },
                    {
                        path: "/auth/reset-password",
                        element: <ResetPasswordPage />,
                    },
                    {
                        path: "/auth/verify-email",
                        element: <VerifyEmailPage />,
                    },
                    {
                        element: <PrivateProviderLayout />,
                        children: [
                            {
                                element: <CalendarProviderLayout />,
                                children: [
                                    {
                                        path: "/demo",
                                        element: <PublicDemoPage />,
                                    },
                                ],
                            },
                        ],
                    },
                    // (Preserved V6.4) Pending Approval route
                    {
                        path: "/pending-approval",
                        element: (
                            <PrivateRoute>
                                <PendingApprovalPage />
                            </PrivateRoute>
                        ),
                    },
                    {
                        element: (
                            <PrivateRoute>
                                <PrivateProviderLayout />
                            </PrivateRoute>
                        ),
                        children: [
                            {
                                element: <AppLayout />,
                                children: [
                                    {
                                        element: <SubscriptionRoute />,
                                        children: [
                                            {
                                                element: <CalendarProviderLayout />,
                                                children: [
                                                    {
                                                        path: "dashboard",
                                                        element: <DashboardPage />,
                                                    },
                                                    {
                                                        path: "session",
                                                        element: <SessionCockpitPage />,
                                                    },
                                                    {
                                                        path: "calendar",
                                                        element: <CalendarView />,
                                                    },
                                                ]
                                            },
                                            {
                                                element: (
                                                    <FeatureRoute
                                                        permission="canAccessAnalytics"
                                                        title="Analytics Access Required"
                                                        description="This workspace is controlled by your current plan permissions. Enable Analytics access in the Master Feature List and assign it to a plan to unlock it."
                                                    />
                                                ),
                                                children: [
                                                    {
                                                        path: "analytics",
                                                        element: <AnalyticsPage />,
                                                    },
                                                    {
                                                        path: "report-card",
                                                        element: <ReportCardPage />,
                                                    },
                                                ]
                                            },
                                            {
                                                element: <BacktestsAccessRoute />,
                                                children: [
                                                    {
                                                        path: "backtests",
                                                        element: <BacktestsPage />,
                                                    },
                                                ]
                                            },
                                            {
                                                path: "trading-system",
                                                element: <TradingSystemPage />,
                                            },
                                            {
                                                path: "trading-systems",
                                                element: <Navigate to="/trading-system" replace />,
                                            },
                                            {
                                                path: "playbooks",
                                                element: <PlaybooksPage />,
                                            },
                                            {
                                                path: "playbooks/:playbookId",
                                                element: <PlaybookDetailPage />,
                                            },
                                            {
                                                path: "playbooks/:playbookId/trades",
                                                element: <PlaybookTradesDrilldownPage />,
                                            },
                                            {
                                                path: "playbooks/missed-trades",
                                                element: <MissedTradesLogPage />,
                                            },
                                            {
                                                path: "playbooks/missed-trades/:logId",
                                                element: <MissedTradeDetailPage />,
                                            },
                                            {
                                                element: (
                                                    <FeatureRoute
                                                        permission="canAccessMindMap"
                                                        title="Mind Map Access Required"
                                                        description="This workspace is controlled by your current plan permissions. Enable Mind Map access in the Master Feature List and assign it to a plan to unlock it."
                                                    />
                                                ),
                                                children: [
                                                    {
                                                        path: "mind-map",
                                                        element: <MindMapPage />,
                                                    },
                                                ]
                                            },
                                            { path: "notebooks", element: <NotebooksPage /> },
                                            { path: "learn", element: <LearnHubPage /> },
                                            { path: "learn/getting-started", element: <LearnGettingStartedPage /> },
                                            { path: "learn/faq", element: <LearnFaqPage /> },
                                            { path: "learn/:topicId", element: <LearnTopicPage /> },
                                            { path: "faq", element: <Navigate to="/learn/faq" replace /> },
                                            { path: "settings/systems", element: <Navigate to="/trading-system" replace /> },
                                            {
                                                path: "settings/:tabId",
                                                element: <SettingsPage />,
                                            },
                                        ]
                                    },
                                    {
                                        path: "reseller",
                                        element: <ResellerDashboardPage />,
                                    },
                                    {
                                        path: "admin",
                                        element: (
                                            <AdminRoute>
                                                <AdminPage />
                                            </AdminRoute>
                                        ),
                                        children: [
                                            {
                                                index: true,
                                                element: <Navigate to="overview" replace />,
                                            },
                                            {
                                                path: "overview",
                                                element: <AdminOverview />,
                                            },
                                            {
                                                path: "security",
                                                element: <AdminSecurityManager />,
                                            },
                                            {
                                                path: "users",
                                                element: <UserManagementDashboard />,
                                            },
                                            {
                                                path: "subscriptions",
                                                element: <SubscriptionManager />,
                                            },
                                            {
                                                path: "billing",
                                                element: <BillingContactManager />,
                                            },
                                            {
                                                path: "links",
                                                element: <LinksManagerPage />,
                                            },
                                            {
                                                path: "legal",
                                                element: <LegalContentManager />,
                                            },
                                            {
                                                path: "features",
                                                element: <FeatureManager />,
                                            },
                                            {
                                                path: "backtests",
                                                element: <BacktestAccessManager />,
                                            },
                                            {
                                                path: "notifications",
                                                element: <NotificationManager />,
                                            },
                                            {
                                                path: "notebook",
                                                element: <AdminNotebook />,
                                            },
                                            {
                                                path: "rates",
                                                element: <ExchangeRateManager />,
                                            },
                                            {
                                                path: "calendar",
                                                element: <CalendarApiManager />,
                                            },
                                            {
                                                path: "assets",
                                                element: <PublicAssetManager />,
                                            },
                                            {
                                                path: "brokers",
                                                element: <BrokerManager />,
                                            },
                                            {
                                                path: "education",
                                                element: <EducationManager />,
                                            },
                                            {
                                                path: "resellers",
                                                element: <ResellerManager />,
                                            },
                                        ]
                                    }
                                ],
                            }
                        ],
                    }
                ],
            }
        ]
    }
];

